import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Panel } from './ApplicantHeader';
import { useHiringApi, errMsg } from './useHiringApi';
import { fmtDateTime } from './hiringConstants';

const FORMATS = [['phone', 'Phone'], ['video', 'Video'], ['in_person', 'In person']];
const OUTCOMES = [['scheduled', 'Scheduled'], ['completed', 'Completed'], ['no_show', 'No-show'], ['cancelled', 'Cancelled']];
const NONE = '__none';

export const Lbl = ({ children, text }) => <label className="block text-xs font-semibold text-zinc-600 space-y-1"><span>{text}</span>{children}</label>;

export default function ApplicantInterviews({ a, cfg, onUpdated }) {
  const api = useHiringApi();
  const [form, setForm] = useState({ scheduled_at: '', format: 'phone', interviewer_id: NONE, notes: '' });
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      onUpdated(await api.addInterview(a.id, { ...form, interviewer_id: form.interviewer_id === NONE ? null : form.interviewer_id, scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null }));
      setForm({ scheduled_at: '', format: 'phone', interviewer_id: NONE, notes: '' });
      toast.success('Interview recorded');
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  const setOutcome = async (iid, outcome) => {
    try { onUpdated(await api.updateInterview(a.id, iid, { outcome })); } catch (err) { toast.error(errMsg(err)); }
  };
  const saveNotes = async (iid, notes) => {
    try { onUpdated(await api.updateInterview(a.id, iid, { notes })); toast.success('Interview notes saved'); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Panel title="Interviews" testId="interviews-panel">
      {(a.interviews || []).length === 0 && <p className="text-xs text-zinc-400">No interviews recorded yet.</p>}
      {(a.interviews || []).map((iv) => (
        <div key={iv.id} className="border border-zinc-100 rounded-lg p-3 space-y-2" data-testid={`interview-${iv.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="font-semibold text-zinc-800">{fmtDateTime(iv.scheduled_at)} · {FORMATS.find((f) => f[0] === iv.format)?.[1] || iv.format || '—'}{iv.interviewer_name ? ` · ${iv.interviewer_name}` : ''}</span>
            <Select value={iv.outcome || 'scheduled'} onValueChange={(v) => setOutcome(iv.id, v)}>
              <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`interview-outcome-${iv.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{OUTCOMES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Textarea rows={2} defaultValue={iv.notes || ''} placeholder="Interview notes…" onBlur={(e) => e.target.value !== (iv.notes || '') && saveNotes(iv.id, e.target.value)} className="text-sm" data-testid={`interview-notes-${iv.id}`} />
        </div>
      ))}
      <form onSubmit={add} className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-100">
        <Lbl text="When"><Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="interview-when" /></Lbl>
        <Lbl text="Format">
          <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}><SelectTrigger data-testid="interview-format"><SelectValue /></SelectTrigger>
            <SelectContent>{FORMATS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
        </Lbl>
        <Lbl text="Interviewer">
          <Select value={form.interviewer_id} onValueChange={(v) => setForm({ ...form, interviewer_id: v })}><SelectTrigger data-testid="interview-interviewer"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value={NONE}>Not set</SelectItem>{(cfg?.staff || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent></Select>
        </Lbl>
        <Lbl text="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" /></Lbl>
        <div className="col-span-2 flex justify-end"><Button className="bg-orange-600 hover:bg-orange-700 text-white" type="submit" size="sm" disabled={saving} data-testid="interview-add">Record interview</Button></div>
      </form>
    </Panel>
  );
}
